<template>
  <div class="d-flex align-center">
    <!-- the bell: a red dot while something is unread, the list in a menu -->
    <v-menu v-model="menu" :close-on-content-click="false" location="bottom end" offset="4">
      <template #activator="{ props }">
        <v-btn v-bind="props" icon variant="text" class="mx-1" :title="unread ? `Уведомления: ${unread} непрочитанных` : 'Уведомления'">
          <v-badge :model-value="unread + invitations.length > 0" color="error" dot>
            <v-icon>{{ unread + invitations.length ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-card width="420" max-height="560" class="d-flex flex-column">
        <v-card-title class="d-flex align-center py-2">
          <span class="text-subtitle-1 font-weight-bold">Уведомления</span>
          <v-spacer></v-spacer>
          <v-btn v-if="unread" size="small" variant="text" color="primary" @click="readAll">Прочитать все</v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <div class="overflow-y-auto flex-grow-1">
          <!-- invitations of the owners of teams: answered on «Мои команды» -->
          <v-list v-if="invitations.length" density="compact" class="py-0">
            <v-list-item
              v-for="inv in invitations"
              :key="`inv-${inv.id}`"
              prepend-icon="mdi-account-multiple-plus"
              :title="`Приглашение в команду «${inv.team_name}»`"
              :subtitle="inv.invited_by ? `Пригласил(а): ${inv.invited_by}` : ''"
              to="/teams"
              @click="menu = false"
            >
              <template #append><v-icon color="error" size="8">mdi-circle</v-icon></template>
            </v-list-item>
            <v-divider></v-divider>
          </v-list>
          <div v-if="!items.length && !invitations.length" class="pa-4 text-medium-emphasis text-center">Уведомлений нет</div>
          <v-expansion-panels v-else v-model="opened" variant="accordion" flat @update:model-value="onOpen">
            <v-expansion-panel v-for="n in items" :key="n.id" :value="n.id">
              <v-expansion-panel-title class="py-2">
                <div class="d-flex flex-column">
                  <span :class="{ 'font-weight-bold': !n.read }">
                    <v-icon v-if="!n.read" color="error" size="8" class="mr-1">mdi-circle</v-icon>{{ n.title }}
                  </span>
                  <span class="text-caption text-medium-emphasis">{{ formatDate(n.published_at) }}</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- the text is written by admins, rendered as the help -->
                <div class="notification-body help-document" v-html="render(n.body)"></div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <v-divider></v-divider>
        <v-checkbox
          :model-value="!popupsOff"
          label="Показывать важные уведомления при входе"
          density="compact"
          hide-details
          class="px-3"
          @update:model-value="v => setPopupsOff(!v)"
        ></v-checkbox>
      </v-card>
    </v-menu>

    <!-- an invitation into a team at a sign-in (the invitations go first): Принять / Отклонить /
         Позже - «Позже» leaves it in the bell and on «Мои команды» -->
    <v-dialog :model-value="!!popup && popup.kind === 'invitation'" max-width="560" persistent>
      <v-card v-if="popup && popup.kind === 'invitation'">
        <v-card-title class="text-h6 text-wrap">Приглашение в команду «{{ popup.inv.team_name }}»</v-card-title>
        <v-card-text>
          <p v-if="popup.inv.invited_by" class="mb-2">Пригласил(а): {{ popup.inv.invited_by }}</p>
          <p class="mb-2">Приняв приглашение, вы станете участником команды: её проекты и источники появятся в переключателе пространств.</p>
          <p class="text-medium-emphasis">Действует до {{ formatDate(popup.inv.expires_at) }}</p>
          <v-alert v-if="inviteError" type="error" variant="tonal" density="compact" class="mt-3">{{ inviteError }}</v-alert>
        </v-card-text>
        <v-card-actions class="px-4 pb-3">
          <span v-if="queue.length" class="text-caption text-medium-emphasis">ещё {{ queue.length }}</span>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="!!answering" @click="laterInvitation">Позже</v-btn>
          <v-btn color="error" variant="text" :loading="answering === 'decline'" :disabled="!!answering" @click="answerInvitation(false)">Отклонить</v-btn>
          <v-btn color="primary" variant="flat" :loading="answering === 'accept'" :disabled="!!answering" @click="answerInvitation(true)">Принять</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- a notification «при входе»: closed by the button or Esc, after that it is read -->
    <v-dialog :model-value="!!popup && popup.kind !== 'invitation'" max-width="640" persistent>
      <v-card v-if="popup && popup.kind !== 'invitation'">
        <v-card-title class="text-h6 text-wrap">{{ popup.title }}</v-card-title>
        <v-card-text>
          <div class="notification-body help-document" v-html="render(popup.body)"></div>
        </v-card-text>
        <v-card-actions class="px-4 pb-3">
          <v-checkbox v-model="dontShow" label="Не показывать уведомления при входе" density="compact" hide-details></v-checkbox>
          <v-spacer></v-spacer>
          <span v-if="queue.length" class="text-caption text-medium-emphasis mr-2">ещё {{ queue.length }}</span>
          <v-btn color="primary" variant="flat" @click="closePopup">Понятно</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiClient from '@/apiClient'
import { renderHelp } from '@/help'
import { takeSignedIn } from '@/utils/signInMark'

// how often the bell asks for new notifications while the page is open
const POLL_MS = 5 * 60 * 1000

export default {
  name: 'NotificationBell',
  data() {
    return {
      items: [],
      invitations: [],
      unread: 0,
      popupsOff: false,
      menu: false,
      opened: null,
      popup: null,
      queue: [],
      dontShow: false,
      answering: '',
      inviteError: '',
      joined: false,
      timer: null,
    }
  },
  async mounted() {
    // the windows only right after a sign-in in this tab (not on a reload, a new tab or a
    // remount); the polling only refreshes the bell and its red dot
    const signedIn = takeSignedIn()
    await this.load()
    // no windows while an admin views a user (task 60)
    const viewing = !!(this.$store.state.userInfo && this.$store.state.userInfo.viewing_as)
    if (signedIn && !this.popupsOff && !viewing) {
      // the invitations into teams first, then the notifications «при входе»
      this.queue = [
        ...this.invitations.map(inv => ({ kind: 'invitation', inv })),
        ...this.items.filter(n => !n.read && n.display === 'login').reverse(),
      ]
      this.nextPopup()
    }
    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') this.load()
    }, POLL_MS)
    window.addEventListener('keydown', this.onKey)
  },
  beforeUnmount() {
    clearInterval(this.timer)
    window.removeEventListener('keydown', this.onKey)
  },
  methods: {
    async load() {
      try {
        const { data } = await apiClient.get('/notifications')
        this.items = data.items
        this.unread = data.unread
        this.popupsOff = data.popups_off
      } catch (e) {
        // the bell stays as it was; the next poll tries again
      }
      try {
        const { data } = await apiClient.get('/teams/invitations/mine')
        this.invitations = data.items || []
      } catch (e) {
        // the invitations stay as they were
      }
    },
    render(text) {
      return renderHelp(text || '')
    },
    formatDate(v) {
      return v ? new Date(v).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
    },
    async markRead(ids) {
      const fresh = ids.filter(id => this.items.some(n => n.id === id && !n.read))
      if (!fresh.length) return
      this.items.forEach(n => { if (fresh.includes(n.id)) n.read = true })
      this.unread = this.items.filter(n => !n.read).length
      try {
        await apiClient.post('/notifications/read', { ids: fresh })
      } catch (e) {
        await this.load()
      }
    },
    onOpen(id) {
      // opening a notification in the list reads it
      if (id != null) this.markRead([id])
    },
    async readAll() {
      this.items.forEach(n => { n.read = true })
      this.unread = 0
      try {
        await apiClient.post('/notifications/read-all')
      } catch (e) {
        await this.load()
      }
    },
    async setPopupsOff(off) {
      this.popupsOff = off
      try {
        await apiClient.put('/notifications/popups', { off })
      } catch (e) {
        await this.load()
      }
    },
    nextPopup() {
      this.popup = this.queue.shift() || null
      this.inviteError = ''
      if (!this.popup && this.joined) {
        // a new team: the switcher of spaces shows it after the page opens anew
        window.location.reload()
      }
    },
    async answerInvitation(accept) {
      const inv = this.popup.inv
      this.answering = accept ? 'accept' : 'decline'
      try {
        await apiClient.post(`/teams/invitations/${inv.id}/${accept ? 'accept' : 'decline'}`)
        if (accept) this.joined = true
        this.invitations = this.invitations.filter(i => i.id !== inv.id)
        this.nextPopup()
      } catch (e) {
        this.inviteError = e.response?.data?.detail || 'Приглашение уже не действует'
        await this.load()
      } finally {
        this.answering = ''
      }
    },
    laterInvitation() {
      // stays in the bell and on «Мои команды»
      this.nextPopup()
    },
    async closePopup() {
      if (!this.popup) return
      const id = this.popup.id
      await this.markRead([id])
      if (this.dontShow) {
        // the rest stay unread in the bell
        this.queue = []
        this.dontShow = false
        await this.setPopupsOff(true)
      }
      this.nextPopup()
    },
    onKey(e) {
      if (e.key !== 'Escape' || !this.popup) return
      if (this.popup.kind === 'invitation') {
        if (!this.answering) this.laterInvitation()
      } else {
        this.closePopup()
      }
    },
  },
}
</script>

<style scoped>
.notification-body {
  font-size: 0.92rem;
}
.notification-body :deep(p), .notification-body :deep(li) { line-height: 1.55; }
.notification-body :deep(p) { margin-bottom: 8px; }
.notification-body :deep(h1) { font-size: 1.2rem; margin-bottom: 8px; }
.notification-body :deep(h2) { font-size: 1.05rem; margin: 12px 0 6px; }
.notification-body :deep(ul), .notification-body :deep(ol) { margin: 0 0 8px 20px; }
.notification-body :deep(code) { background: rgba(0, 0, 0, 0.06); border-radius: 4px; padding: 1px 5px; }
.v-btn {
  text-transform: none;
}
</style>
